/** DUPLICATE = COPY CONFIGURATION AS NEW DRAFT (never copy execution). */

import type { Edge } from '@xyflow/react'
import { copyIncomingEdgesForFork, remapForkPromptFields } from './canvasRegenerateFork'
import { resetGenerationStatusToDraft } from './canvasGenerationExecutionStrip'
import { buildGenerateCreativeDuplicateFields } from './generateCreativeCanvas'
import { buildGenerateTextDuplicateFields } from './generateTextCanvas'
import type { FlowNodeData } from './types'

const GENERATION_NODE_TYPES = new Set([
  'generate_image',
  'generate_video',
  'generate_voice_template',
  'generate_voice',
  'generate_tryon',
  // VISTOERA_GENERATE_CREATIVE_CANONICAL_GENERATION_CARD_UI_DIVERGE_
  // TRIPLET_AND_SINGLE_EXPANSION_V3 §39/§40 — Duplicate now goes through
  // this SAME shared "copy params, never copy execution" contract every
  // other generation node already uses (Header button, never a second
  // Footer control — spec §11/§39).
  'generate_creative',
  'generate_text',
])

export function isGenerationNodeType(nodeType: string | undefined): boolean {
  return !!nodeType && GENERATION_NODE_TYPES.has(nodeType)
}

export function buildGenerationDuplicateFields(
  nodeType: string,
  originFields: Record<string, unknown> | undefined,
  edgeIdMap: Map<string, string>,
): Record<string, unknown> {
  // `generate_creative`'s field shape has no `status`/prompt-mention
  // fields at all (its own `runStatus`/`divergeRunStatus`/`currentResult`
  // convention, spec §1/§37) — `resetGenerationStatusToDraft`'s
  // `status: 'READY'` convention doesn't apply, so it gets its own
  // dedicated reset rather than being silently no-op'd through the
  // media-node-shaped helpers below.
  if (nodeType === 'generate_creative') {
    return buildGenerateCreativeDuplicateFields(originFields || {})
  }
  if (nodeType === 'generate_text') {
    return buildGenerateTextDuplicateFields(originFields || {}, edgeIdMap)
  }
  // VistoeraTASK-00360 — Duplicate copies configuration only (never
  // generation results or runtime/job state). Diverge/regenerate-as-new
  // still uses `resetGenerationStatusToDraft` directly at call sites.
  const draft = resetGenerationStatusToDraft(originFields || {})
  return remapForkPromptFields(nodeType, draft, edgeIdMap)
}

export interface GenerationDuplicatePayload {
  nodeData: FlowNodeData
  copiedEdges: Edge[]
}

export function buildGenerationDuplicatePayload(
  originData: FlowNodeData,
  newNodeId: string,
  sourceNodeId: string,
  edges: Edge[],
  newEdgeId: () => string,
): GenerationDuplicatePayload {
  const { copiedEdges, edgeIdMap } = copyIncomingEdgesForFork(edges, sourceNodeId, newNodeId, newEdgeId)
  const fields = buildGenerationDuplicateFields(originData.nodeType, originData.fields, edgeIdMap)
  return {
    copiedEdges,
    nodeData: {
      nodeType: originData.nodeType,
      category: originData.category,
      entityType: originData.entityType,
      entityId: originData.entityId,
      collapsed: originData.collapsed,
      latestRunId: null,
      fields,
    },
  }
}
